// Pre-plan look at the goal text: does it hold several separate goals, and
// does it name specifics (a school's projects, "my backlog") only the user can
// fill in? Pure TS; the model call lives in onboarding.ts.
import z from 'zod'

import { GOAL_TITLE_MAX, shortenTitle } from './planQuality'

export type GoalAnalysis = {
  goals: string[]
  // goal: index into `goals` the question is about (-1 or unset: the whole text).
  questions: Array<{ question: string; hint?: string; goal?: number }>
}

export const EMPTY_ANALYSIS: GoalAnalysis = { goals: [], questions: [] }
export const MAX_SPLIT_GOALS = 5
export const MAX_DETAIL_QUESTIONS = 2
// Short single-outcome inputs ("Run a half marathon in March") skip the call.
export const MIN_ANALYZE_CHARS = 25

export function goalAnalysisPrompt(goalText: string, locale = 'en', context = ''): string {
  return `You prepare goals for a goal-planning app. Read the user's goal text and answer in JSON.

"goals": does the text contain SEVERAL DISTINCT goals — separate outcomes in different life areas that each deserve their own plan (e.g. "improve my finances, finish my coding school milestone, clear my course backlog and find an internship")? If so, list each one as a short goal title (at most 50 characters, outcome-focused, keep a deadline that applies to it). If it is ONE goal — even with several steps, parts, a reason or a deadline ("Run a half marathon in March", "Learn Spanish so I can travel to Mexico", "Launch my app and get 100 users") — return an empty array. Never split the steps of one goal.

"questions": 0 to 2 short questions, ONLY when the text refers to specific things a planner cannot turn into concrete tasks without the user's answer: which projects/modules/courses are left, what is in "my backlog", which exam, which program. Ask exactly what is missing (e.g. "Which projects are left in your 1337 milestone?"). Use world knowledge to make the question precise (name the school or program), but don't ask about things you already know. Never ask about the deadline, experience level, time available, schedule or motivation — those are asked elsewhere. A clear goal needs no questions: return an empty array. When "goals" is not empty, each question's "goal" is the index (from 0) of the goal it is about, else -1. Each question has a "hint": a short example answer with real names when you know the context (e.g. for 1337: "ft_printf, get_next_line, push_swap") — never placeholders like "Project A".

Write titles, questions and hints in the language of the goal text${locale !== 'en' ? ` (the app language is "${locale}")` : ''}.
${context ? `\n${context}\n` : ''}
Goal text: """${goalText.slice(0, 1500)}"""`
}

export const GOAL_ANALYSIS_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['goals', 'questions'],
  properties: {
    goals: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['title'],
        properties: { title: { type: 'string' } },
      },
    },
    questions: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['question', 'hint', 'goal'],
        properties: {
          question: { type: 'string' },
          hint: { type: 'string' },
          goal: { type: 'integer' },
        },
      },
    },
  },
} as const

const rawSchema = z.object({
  goals: z
    .array(z.union([z.string(), z.object({ title: z.string() }).transform((g) => g.title)]))
    .catch([]),
  questions: z
    .array(
      z.union([
        z
          .string()
          .transform((question) => ({ question, hint: '', goal: undefined as number | undefined })),
        z.object({
          question: z.string(),
          hint: z.string().optional().catch(undefined),
          goal: z.number().int().optional().catch(undefined),
        }),
      ]),
    )
    .catch([]),
})

export function parseGoalAnalysis(raw: unknown, locale?: string): GoalAnalysis {
  const parsed = rawSchema.safeParse(raw)
  if (!parsed.success) return EMPTY_ANALYSIS
  const seen = new Set<string>()
  // Kept titles with the model's index, so a question's `goal` survives the dedupe.
  const kept = parsed.data.goals
    .map((g, from) => ({ title: shortenTitle(g, GOAL_TITLE_MAX, locale), from }))
    .filter(({ title }) => title && !seen.has(title.toLowerCase()) && seen.add(title.toLowerCase()))
    .slice(0, MAX_SPLIT_GOALS)
  const goals = kept.map((k) => k.title)
  const split = goals.length >= 2
  const questions = parsed.data.questions
    .map(({ question, hint, goal }) => {
      const at = split ? kept.findIndex((k) => k.from === goal) : -1
      return {
        question: question.trim().slice(0, 200),
        ...(hint?.trim() && { hint: hint.trim().slice(0, 120) }),
        ...(at >= 0 && { goal: at }),
      }
    })
    .filter((q) => q.question.length > 3)
    .slice(0, MAX_DETAIL_QUESTIONS)
  return { goals: split ? goals : [], questions }
}

// Clarifying answers as plan-prompt context; skipped ones are dropped.
export function detailsPrompt(details?: Array<{ question: string; answer: string }>): string {
  const lines = (details ?? [])
    .map((d) => ({ q: d.question.trim().slice(0, 200), a: d.answer.trim().slice(0, 600) }))
    .filter((d) => d.q && d.a)
  return lines.length
    ? `DETAILS THE USER GAVE (where they apply to this goal, build the tasks from them and name each item they listed):\n${lines.map((d) => `- ${d.q} → ${d.a}`).join('\n')}`
    : ''
}

// One-line note for coach/MCP results when the input looked like several goals.
export function splitHint(goals: string[]): string | undefined {
  return goals.length >= 2
    ? `This request looked like ${goals.length} separate goals (${goals.map((g) => `"${g}"`).join(', ')}). It was created as one goal; ask the user whether they'd rather split it into separate goals.`
    : undefined
}
